import * as _ from 'underscore';
import { ICreateScheduleParams } from '../ServerApi/requests/CreateScheduleRequest';
import { IUpdateScheduleParams } from '../ServerApi/requests/UpdateScheduleRequest';
import {
  IDailySchedule,
  IHourlySchedule,
  IMonthlySchedule,
  ISchedule,
  IScheduleDetails,
  IScheduleFrequency,
  IWeeklySchedule,
  ScheduleActions,
  ScheduleLastDayOfMonth,
  ScheduleTypes,
} from '../ServerApi/types/ISchedule';
import {
  FrequencyOption,
  IFrequencyOption,
  IScheduleFrequencyViewModel,
  IScheduleViewModel,
} from './CreateOrEditScheduleCtrl';

export module CreateOrEditScheduleViewModelMapper {

  export function defaultSchedule(): IScheduleViewModel {
    return _.extend({
      name: '',
      scheduledAction: ScheduleActions.Extract,
      priority: 50,
      parallel: true,
    }, this.defaultScheduleFrequency());
  }

  export function defaultScheduleFrequency(): IScheduleFrequencyViewModel {
    return {
      scheduleType: ScheduleTypes.Hourly,
      hourly: {
        startTime: 0,
        endTime: 0,
        recurrenceMinutes: 60
      },
      daily: {
        startTime: 0
      },
      weekly: {
        startTime: 0,
        recurrenceDaysOfWeek: []
      },
      monthly: {
        startTime: 0,
        recurrenceDayOfMonth: ScheduleLastDayOfMonth
      },
      frequencyOptions: [
        FrequencyOption.Hourly,
        FrequencyOption.Daily,
        FrequencyOption.Weekly,
        FrequencyOption.Monthly
      ]
    };
  }

  export function viewModelFromSchedule(schedule: ISchedule): IScheduleViewModel {
    const viewModel = this.defaultSchedule();
    viewModel.id = schedule.id;
    viewModel.name = schedule.name;
    viewModel.scheduleType = schedule.scheduleType;
    viewModel.scheduledAction = schedule.scheduledAction;
    viewModel.priority = schedule.priority;
    viewModel.parallel = schedule.parallel;

    return _.extend(viewModel, this.viewModelFromScheduleFrequency({scheduleType: schedule.scheduleType, scheduleDetails: schedule.scheduleDetails}));
  }

  export function viewModelFromScheduleFrequency(schedule: IScheduleFrequency, frequencyOptions?: IFrequencyOption[]): IScheduleViewModel {
    const viewModel = this.defaultScheduleFrequency();
    viewModel.scheduleType = schedule.scheduleType;

    switch (schedule.scheduleType) {
      case ScheduleTypes.Hourly:
        viewModel.hourly = <IHourlySchedule> _.clone(schedule.scheduleDetails);
        break;
      case ScheduleTypes.Daily:
        viewModel.daily = <IDailySchedule> _.clone(schedule.scheduleDetails);
        break;
      case ScheduleTypes.Weekly:
        viewModel.weekly = {
          startTime: schedule.scheduleDetails.startTime,
          recurrenceDaysOfWeek: _.clone((<IWeeklySchedule>schedule.scheduleDetails).recurrenceDaysOfWeek)
        };
        break;
      case ScheduleTypes.Monthly:
        viewModel.monthly = <IMonthlySchedule> _.clone(schedule.scheduleDetails);
        break;
    }

    if (frequencyOptions){
      viewModel.frequencyOptions = frequencyOptions;
    }

    return viewModel;
  }

  export function createScheduleParamsFromViewModel(viewModel: IScheduleViewModel): ICreateScheduleParams {
    return {
      name: viewModel.name,
      priority: viewModel.priority,
      scheduleType: viewModel.scheduleType,
      parallel: viewModel.parallel,
      scheduledAction: viewModel.scheduledAction,
      scheduleDetails: scheduleDetailsFromViewModel(viewModel)
    };
  }

  export function updateScheduleParamsFromViewModel(viewModel: IScheduleViewModel): IUpdateScheduleParams {
    return {
      id: viewModel.id,
      name: viewModel.name,
      priority: viewModel.priority,
      scheduleType: viewModel.scheduleType,
      parallel: viewModel.parallel,
      scheduleDetails: scheduleDetailsFromViewModel(viewModel)
    };
  }

  export function scheduleFrequencyFromViewModel(viewModel: IScheduleFrequencyViewModel): IScheduleFrequency {
    return {
      scheduleType: viewModel.scheduleType,
      scheduleDetails: scheduleDetailsFromViewModel(viewModel)
    };
  }

  function scheduleDetailsFromViewModel(viewModel: IScheduleFrequencyViewModel): IScheduleDetails {
    switch (viewModel.scheduleType) {

      case ScheduleTypes.Daily:
        return viewModel.daily;

      case ScheduleTypes.Hourly:
        return viewModel.hourly;

      case ScheduleTypes.Weekly:
        return viewModel.weekly;

      case ScheduleTypes.Monthly:
        return viewModel.monthly;
    }
    throw 'Unknown scheduleType: ' + viewModel.scheduleType;
  }

}
